import { h, type Component, type VNode } from 'vue'

export interface AsyncModalStateOptions {
  /**
   * Whether this modal is still the one the user is looking at.
   *
   * The async wrapper renders its fallback purely on its own load state, and
   * `activeModal` in the UI store is a single slot — so opening the mobile menu
   * mid-fetch would otherwise leave a spinner (or worse, a blocking error card)
   * painted over unrelated UI for the rest of a ~21 s Slow-4G fetch.
   */
  isVisible?: () => boolean
  /**
   * Close the modal. While loading this is Escape cancelling a slow fetch (the
   * fetch keeps running, so reopening lands on the chunk it was already
   * pulling); on the error card it is the user dismissing a failure they have
   * chosen not to act on.
   */
  onClose?: () => void
}

export interface AsyncModalStates {
  loadingComponent: () => VNode | null
  delay: number
  suspensible: false
  errorComponent: () => VNode | null
}

/**
 * Loading / error states for an async modal that is fetched on first open.
 *
 * Since #819 the deposit and withdraw modals are mounted on first open rather
 * than at page load, which keeps the LiFi widget's React stack (~2.1 MB
 * unpacked) out of the startup bundle. The cost lands on the first tap: that
 * fetch queues behind whatever the game is still streaming, measured at 361 ms
 * on a fast connection, 1.2 s on 4G and up to ~21 s on Slow 4G. Without a
 * loading state the button simply looks dead for that whole window.
 *
 * Three of these options are load-bearing, and each was arrived at by watching
 * the spinner fail to appear:
 *
 * - **`suspensible: false`** — without it none of this renders at all. Nuxt
 *   wraps the app tree in `<Suspense>`, and a suspensible async component hands
 *   its pending state to the nearest boundary rather than showing its own
 *   `loadingComponent`.
 * - **`errorComponent` / `loadingComponent` as functions** — they take a
 *   *component*; passing a bare `h(...)` VNode is accepted without warning and
 *   invalidates the whole options object, taking the other state down with it.
 *   Functions are also what lets the fallback receive props, which a bare
 *   component reference cannot: Vue builds that one from the parent's props,
 *   and a `v-if`-gated modal has none.
 * - **`delay`** — keeps the spinner from flashing when the chunk is already
 *   cached, i.e. every open after the first.
 *
 * There is no `timeout`: on the Slow-4G tail this fetch legitimately runs for
 * ~21 s, and a timeout there would convert a request that was going to succeed
 * into a permanent error. A request that never settles is covered by
 * `onClose` — Escape dismisses the spinner without killing the fetch.
 *
 * **There is no `onError` retry either, and this is the part that is easy to
 * add back by mistake.** A failed chunk load cannot be retried inside the
 * current document at all. When fetching an ES module fails, the browser
 * records that failure in the document's module map, and every later `import()`
 * of the same URL is rejected from that record without touching the network.
 * Measured against a local server that counts requests: four sequential
 * imports, three of them after connectivity was restored, produced **one**
 * request and four failures. So `retry()` re-runs the loader, the loader
 * re-runs `import()`, and the module map rejects it instantly — Vue reports a
 * retry that never left the page. Recovery needs a new document, which is why
 * the error state offers a reload (#845; the same reason Nuxt's own
 * `chunk-reload.client.ts` reloads rather than re-importing).
 *
 * Shared rather than inlined so the deposit modal, the withdraw modal and the
 * LiFi widget cannot drift apart, and so the contract is assertable in a test
 * without mounting `app.vue`.
 */
export function asyncModalStates(
  fallback: Component,
  { isVisible, onClose }: AsyncModalStateOptions = {}
): AsyncModalStates {
  const visible = (): boolean => isVisible?.() ?? true

  return {
    loadingComponent: () => (visible() ? h(fallback, { onClose }) : null),
    delay: 150,
    suspensible: false,
    errorComponent: () => (visible() ? h(fallback, { failed: true, onClose }) : null),
  }
}
